import { Star } from '@ui/components/icons';
import { isAddressInVipAllowlist } from '@ui/constants/allowlist';
import { Icon } from '@ui/lib/park/ui/icon';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { HTMLStyledProps, styled } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

import { useVipModalStore } from '../store/useVipModalStore';

const VIP_GOLD = '#FFD874';

/**
 * Compact header-bar VIP entry ("VIP"), shown only for VIP wallets.
 * Hidden permanently once the user messages the VIP manager from the modal
 * (see dismissHeaderButton). The wallet-menu and portfolio entries remain.
 * Accepts style props so callers control visibility (e.g. mobile vs desktop).
 */
export const HeaderVipButton = (p: HTMLStyledProps<'button'>) => {
  const { t } = useTranslation();
  const { address, isConnected } = useAccount();
  const open = useVipModalStore((s) => s.open);
  const dismissed = useVipModalStore((s) => s.headerButtonDismissed);
  const hydrateHeaderDismissed = useVipModalStore(
    (s) => s.hydrateHeaderDismissed,
  );

  // Sync the persisted choice from localStorage on the client.
  useEffect(() => {
    hydrateHeaderDismissed();
  }, [hydrateHeaderDismissed]);

  if (!isConnected || !isAddressInVipAllowlist(address)) return null;
  if (dismissed) return null;

  return (
    <styled.button
      type="button"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap={{ base: '0', desktop: '6px' }}
      // sized to match the "Enable 1CT" header button (Button size=small):
      // 32px tall, 12px/600 label, radius 2
      h="8"
      w={{ base: '8', desktop: 'auto' }}
      px={{ base: '0', desktop: '10px' }}
      flexShrink={0}
      textStyle="headings.xsmall"
      border="1px solid"
      borderRadius="2px"
      color={VIP_GOLD}
      borderColor="rgba(255, 216, 116, 0.45)"
      background="linear-gradient(120deg, rgba(255,216,116,0.10), rgba(255,216,116,0.02))"
      cursor="pointer"
      transition="all 200ms ease"
      _hover={{ borderColor: 'rgba(255, 216, 116, 0.85)' }}
      onClick={() => open()}
      {...p}
    >
      <Icon boxSize="16px" color={VIP_GOLD}>
        <Star />
      </Icon>
      <styled.span display={{ base: 'none', desktop: 'inline' }}>
        {t('vip.short', 'VIP')}
      </styled.span>
    </styled.button>
  );
};
